import { takeLatest, call, put, select } from 'redux-saga/effects';
import {
    postLabOrderSuccess, postLabOrderFailure,
    getLabOrderSuccess, getLabOrderFailure,
    getLabOrderByIdSuccess, getLabOrderByIdFailure,
    getLabOrderByPatientSuccess, getLabOrderByPatientFailure,
    updateLabOrderStatusSuccess, updateLabOrderStatusFailure,
    cancelLabOrderSuccess, cancelLabOrderFailure,
    addTestsSuccess, addTestsFailure,
    cancelTestSuccess, cancelTestFailure,
    editOrderSuccess, editOrderFailure,
    bulkReleaseLabOrderSuccess, bulkReleaseLabOrderFailure,
} from './slice';
import requestHandler from '../../../../utils/requestHandler';
import { logoutRequest } from '../../auth/authSlice';

const getAuthHeaders = (authToken, branchId) => ({
    "Authorization": authToken,
    "Content-Type": "application/json",
    "X-Current-Branch": branchId,
});

function* postRequest(action) {
    try {
        const currentBranch = yield select(state => state.branch.currentBranch);
        const requestDetail = {
            method: "POST",
            headers: getAuthHeaders(localStorage.getItem("item"), currentBranch.id),
            body: JSON.stringify(action.payload)
        };
        const response = yield call(requestHandler, `${process.env.REACT_APP_SERVER_API}api/laboratory/orders`, requestDetail);
        yield put(postLabOrderSuccess(response));
    } catch (error) { yield put(postLabOrderFailure(error?.response?.message || error)); }
}

function* getRequest(action) {
    try {
        let authToken = localStorage.getItem("item");
        if (!authToken) { yield put(logoutRequest()); return; }
        const { page, limit, searchQuery, status, priority, dateFrom, dateTo, testId, panelId } = action.payload || {};
        const currentBranch = yield select(state => state.branch.currentBranch);
        let url = `${process.env.REACT_APP_SERVER_API}api/laboratory/orders?page=${page || 1}&limit=${limit || 10}&searchQuery=${encodeURIComponent(searchQuery || '')}`;
        if (status) url += `&status=${encodeURIComponent(status)}`;
        if (priority) url += `&priority=${encodeURIComponent(priority)}`;
        if (dateFrom) url += `&dateFrom=${encodeURIComponent(dateFrom)}`;
        if (dateTo) url += `&dateTo=${encodeURIComponent(dateTo)}`;
        if (testId) url += `&testId=${encodeURIComponent(testId)}`;
        if (panelId) url += `&panelId=${encodeURIComponent(panelId)}`;
        const requestDetail = {
            method: "GET",
            headers: getAuthHeaders(authToken, currentBranch.id)
        };
        const response = yield call(requestHandler, url, requestDetail);
        yield put(getLabOrderSuccess(response));
    } catch (error) { yield put(getLabOrderFailure(error?.response?.message || error)); }
}

function* getByIdRequest(action) {
    try {
        let authToken = localStorage.getItem("item");
        if (!authToken) { yield put(logoutRequest()); return; }
        const currentBranch = yield select(state => state.branch.currentBranch);
        const requestDetail = {
            method: "GET",
            headers: getAuthHeaders(authToken, currentBranch.id)
        };
        const response = yield call(requestHandler, `${process.env.REACT_APP_SERVER_API}api/laboratory/orders/${action.payload}`, requestDetail);
        yield put(getLabOrderByIdSuccess(response));
    } catch (error) { yield put(getLabOrderByIdFailure(error?.response?.message || error)); }
}

function* getByPatientRequest(action) {
    try {
        let authToken = localStorage.getItem("item");
        if (!authToken) { yield put(logoutRequest()); return; }
        const currentBranch = yield select(state => state.branch.currentBranch);
        const requestDetail = {
            method: "GET",
            headers: getAuthHeaders(authToken, currentBranch.id)
        };
        const response = yield call(requestHandler, `${process.env.REACT_APP_SERVER_API}api/laboratory/orders/patient/${action.payload}`, requestDetail);
        yield put(getLabOrderByPatientSuccess(response));
    } catch (error) { yield put(getLabOrderByPatientFailure(error?.response?.message || error)); }
}

function* updateStatusRequest(action) {
    try {
        const { id, status } = action.payload;
        const currentBranch = yield select(state => state.branch.currentBranch);
        const requestDetail = {
            method: "PUT",
            headers: getAuthHeaders(localStorage.getItem("item"), currentBranch.id),
            body: JSON.stringify({ status })
        };
        const response = yield call(requestHandler, `${process.env.REACT_APP_SERVER_API}api/laboratory/orders/${id}/status`, requestDetail);
        yield put(updateLabOrderStatusSuccess(response));
    } catch (error) { yield put(updateLabOrderStatusFailure(error?.response?.message || error)); }
}

function* cancelRequest(action) {
    try {
        const { id, reason } = action.payload;
        const currentBranch = yield select(state => state.branch.currentBranch);
        const requestDetail = {
            method: "PUT",
            headers: getAuthHeaders(localStorage.getItem("item"), currentBranch.id),
            body: JSON.stringify({ reason })
        };
        const response = yield call(requestHandler, `${process.env.REACT_APP_SERVER_API}api/laboratory/orders/${id}/cancel`, requestDetail);
        yield put(cancelLabOrderSuccess(response));
    } catch (error) { yield put(cancelLabOrderFailure(error?.response?.message || error)); }
}

function* addTestsRequest(action) {
    try {
        const { id, tests, panelIds } = action.payload;
        const currentBranch = yield select(state => state.branch.currentBranch);
        const requestDetail = {
            method: "PUT",
            headers: getAuthHeaders(localStorage.getItem("item"), currentBranch.id),
            body: JSON.stringify({ tests, panelIds })
        };
        const response = yield call(requestHandler, `${process.env.REACT_APP_SERVER_API}api/laboratory/orders/${id}/add-tests`, requestDetail);
        yield put(addTestsSuccess(response));
    } catch (error) { yield put(addTestsFailure(error?.response?.message || error)); }
}

function* cancelTestRequest(action) {
    try {
        const { orderId, testEntryId, reason } = action.payload;
        const currentBranch = yield select(state => state.branch.currentBranch);
        const requestDetail = {
            method: "PUT",
            headers: getAuthHeaders(localStorage.getItem("item"), currentBranch.id),
            body: JSON.stringify({ reason })
        };
        const response = yield call(requestHandler, `${process.env.REACT_APP_SERVER_API}api/laboratory/orders/${orderId}/tests/${testEntryId}/cancel`, requestDetail);
        yield put(cancelTestSuccess(response));
    } catch (error) { yield put(cancelTestFailure(error?.response?.message || error)); }
}

function* editOrderRequest(action) {
    try {
        const { id, ...data } = action.payload;
        const currentBranch = yield select(state => state.branch.currentBranch);
        const requestDetail = {
            method: "PUT",
            headers: getAuthHeaders(localStorage.getItem("item"), currentBranch.id),
            body: JSON.stringify(data)
        };
        const response = yield call(requestHandler, `${process.env.REACT_APP_SERVER_API}api/laboratory/orders/${id}/edit`, requestDetail);
        yield put(editOrderSuccess(response));
    } catch (error) { yield put(editOrderFailure(error?.response?.message || error)); }
}

function* bulkReleaseRequest(action) {
    try {
        const currentBranch = yield select(state => state.branch.currentBranch);
        const requestDetail = {
            method: "PUT",
            headers: getAuthHeaders(localStorage.getItem("item"), currentBranch.id),
            body: JSON.stringify(action.payload)
        };
        const response = yield call(requestHandler, `${process.env.REACT_APP_SERVER_API}api/laboratory/orders/bulk-release`, requestDetail);
        yield put(bulkReleaseLabOrderSuccess(response));
    } catch (error) { yield put(bulkReleaseLabOrderFailure(error?.response?.message || error)); }
}

function* saga() {
    yield takeLatest('labOrder/postLabOrderRequest', postRequest);
    yield takeLatest('labOrder/getLabOrderRequest', getRequest);
    yield takeLatest('labOrder/getLabOrderByIdRequest', getByIdRequest);
    yield takeLatest('labOrder/getLabOrderByPatientRequest', getByPatientRequest);
    yield takeLatest('labOrder/updateLabOrderStatusRequest', updateStatusRequest);
    yield takeLatest('labOrder/cancelLabOrderRequest', cancelRequest);
    yield takeLatest('labOrder/addTestsRequest', addTestsRequest);
    yield takeLatest('labOrder/cancelTestRequest', cancelTestRequest);
    yield takeLatest('labOrder/editOrderRequest', editOrderRequest);
    yield takeLatest('labOrder/bulkReleaseLabOrderRequest', bulkReleaseRequest);
}

export default saga;
